/**
 * statementTotals.ts — what a customer has bought, paid and still owes.
 *
 * The statement used to sum `totalAmount`, `paidAmount` and `dueAmount` across
 * every invoice carrying the customer's id, with no filter on type or status.
 * Three things followed:
 *
 *   - A return INCREASED displayed purchases. A credit note is an invoice with
 *     a positive totalAmount; summed blind, refunding goods looked like buying
 *     more of them.
 *   - A refund counted as the customer PAYING. A credit note's paidAmount is
 *     the money handed back across the counter.
 *   - A voided unpaid sale stayed in outstanding dues. Voiding cancels the sale;
 *     it does not clear the dueAmount already written on the row.
 *
 * The model here is the one the server reconciles balances with (see
 * backend-api/scripts/reconcile-customer-balances.ts), so the khata screen and
 * the authoritative balance agree:
 *
 *   per invoice   SALE:         + (total − paid)
 *                 CREDIT_NOTE:  − (total − paid)
 *   voided rows excluded entirely
 *
 * Read the credit-note term as: a return reduces what is owed, and a refund
 * paying it out in cash puts that reduction back, because the shop handed the
 * money over instead of crediting the account.
 *
 * Pure — the screens fetch, this decides.
 */

export interface StatementInvoice {
    type: string;
    status: string;
    totalAmount: number;
    paidAmount: number;
}

export interface StatementTotals {
    /** Goods bought, net of returns. */
    purchased: number;
    /** Money the customer handed over, net of refunds handed back. */
    paid: number;
    /** Still owed. Never negative — a customer in credit owes nothing. */
    due: number;
    /** Returns, surfaced separately so the netting is visible rather than implied. */
    returned: number;
}

/** Statuses that mean the invoice never counted. Mirrors the sync service. */
export const VOIDED_STATEMENT_STATUSES: readonly string[] = ['VOID', 'VOIDED', 'CANCELLED'];

export function computeStatementTotals(invoices: StatementInvoice[]): StatementTotals {
    let purchased = 0;
    let paid = 0;
    let due = 0;
    let returned = 0;

    for (const inv of invoices) {
        if (VOIDED_STATEMENT_STATUSES.includes(inv.status)) continue;

        const total = inv.totalAmount;
        const settled = inv.paidAmount;

        if (inv.type === 'CREDIT_NOTE') {
            returned += total;
            purchased -= total;
            // Money going the other way across the counter.
            paid -= settled;
            due -= total - settled;
        } else {
            purchased += total;
            paid += settled;
            due += total - settled;
        }
    }

    return {
        purchased,
        paid,
        due: Math.max(0, due),
        returned,
    };
}

/** True when this invoice should appear in the statement's line listing. */
export function isStatementVisible(inv: StatementInvoice): boolean {
    return !VOIDED_STATEMENT_STATUSES.includes(inv.status);
}
